import { PropsWithChildren, useMemo, useRef } from "react"
import cn from "classnames"
import { useClickAway, useToggle } from "react-use"
import { CSSTransition } from "react-transition-group"

import { useInputDevicesContext } from "modules/Call/InputDevices"
import { Button, Text } from "ui-kit/components-branded/index"
import { Icon } from "components"
import { getHostname } from "utils/helpers"
import { browser } from "utils"
import { APP_PAGE_URL } from "utils/env-management"

type microphoneIconType = "microphone-call" | "microphone-call-off" | "microphone-call-no-access"
type videoIconType = "video-call" | "video-call-off" | "video-call-no-access"

interface LinkProps {
    className?: string
    href: string
}

const Link = ({ className, children, href }: PropsWithChildren<LinkProps>) => {
    return (
        <div className={cn("access-devices__link", className)}>
            <a href={href} target="_blank" rel="noreferrer">
                <Text className="access-devices__link-text mr-2">{children}</Text>
                <Icon type="arrow-short-right" size={16} />
            </a>
        </div>
    )
}

const GiveAccessLink = ({ onClick }: { onClick: (value: boolean) => void }) => {
    return (
        <Text className="access-devices__give-access" onClick={() => onClick(true)}>
            Как дать доступ
        </Text>
    )
}

const DefaultLinks = ({ isOther }: { isOther?: boolean }) => {
    const browserAccessText = isOther ? "О доступе к устройствам" : "Подробнее"

    return (
        <div
            className={cn({
                "access-devices__default-links": isOther,
            })}
        >
            <Link
                href="https://psyalter.helpdeskeddy.com/ru/knowledge_base/art/296/cat/30/"
                className={cn({
                    "mb-3": !isOther,
                })}
            >
                {browserAccessText}
            </Link>
            <Link href="https://psyalter.helpdeskeddy.com/ru/knowledge_base/article/298/category/30/">
                Как проверить что устройства работают
            </Link>
            <Link href="https://psyalter.helpdeskeddy.com/ru/knowledge_base/art/297/cat/30/">
                Технические требования
            </Link>
        </div>
    )
}

export function AccessDevices() {
    const [{ microphoneEnabled, cameraEnabled, microphoneAccessed, cameraAccessed }] =
        useInputDevicesContext()

    const modalRef = useRef<HTMLDivElement>(null)
    const [showAccessModal, toggleShowAccessModal] = useToggle(false)

    const hostName = useMemo(() => getHostname(APP_PAGE_URL), [])

    function hideModal() {
        toggleShowAccessModal(false)
    }
    function showModal() {
        toggleShowAccessModal(true)
    }

    useClickAway(modalRef, hideModal)

    const microphoneContent = useMemo<{ text: string; icon: microphoneIconType }>(() => {
        if (!microphoneAccessed) {
            return {
                text: "Нет доступа к микрофону",
                icon: "microphone-call-no-access",
            }
        }
        if (!microphoneEnabled) {
            return {
                text: "Микрофон выключен",
                icon: "microphone-call-off",
            }
        }
        return {
            text: "Микрофон включен",
            icon: "microphone-call",
        }
    }, [microphoneEnabled, microphoneAccessed])

    const videoContent = useMemo<{ text: string; icon: videoIconType }>(() => {
        if (!cameraAccessed) {
            return {
                text: "Нет доступа к камере",
                icon: "video-call-no-access",
            }
        }

        if (!cameraEnabled) {
            return {
                text: "Камера выключена",
                icon: "video-call-off",
            }
        }

        return {
            text: "Камера включена",
            icon: "video-call",
        }
    }, [cameraEnabled, cameraAccessed])

    const toolTipText = useMemo(() => {
        if (browser.browser.name === "Chrome") {
            if (browser.os.name === "iOS") {
                return (
                    <>
                        <Text>
                            Обновите страницу и&nbsp;когда увидите вопрос, дайте доступ
                            к&nbsp;устройствам.
                        </Text>
                        <DefaultLinks />
                    </>
                )
            }
            if (browser.os.name === "Android") {
                return (
                    <>
                        <Text>
                            Еще&nbsp;&rsaquo; Настройки&nbsp;&rsaquo; Настройки сайтов&nbsp;&rsaquo;
                            Микрофон или Камера&nbsp;&rsaquo; {hostName}&nbsp;&rsaquo; Разрешить
                        </Text>
                        <DefaultLinks />
                    </>
                )
            }
            return (
                <>
                    <Text>
                        Настройки сайтов&nbsp;&rsaquo; {hostName}&nbsp;&rsaquo; Камера&nbsp;&frasl;
                        Микрофон&nbsp;&rsaquo; Разрешить
                    </Text>
                    <DefaultLinks />
                </>
            )
        }
        if (browser.browser.name === "Safari") {
            if (browser.os.name === "iOS") {
                return (
                    <>
                        <Text>
                            Обновите страницу и&nbsp;когда увидите вопрос, дайте доступ
                            к&nbsp;устройствам.
                        </Text>
                        <DefaultLinks />
                    </>
                )
            }
            if (browser.os.name === "macOS") {
                return (
                    <>
                        <Text>
                            Safari&nbsp;&rsaquo; Настройки&nbsp;&rsaquo; Камера или Микрофон
                            &nbsp;&rsaquo; {hostName}
                            &nbsp;&rsaquo; Разрешать всегда
                        </Text>
                        <DefaultLinks />
                    </>
                )
            }
        }
        return <DefaultLinks isOther />
    }, [microphoneAccessed, cameraAccessed])

    return (
        <div className="access-devices mt-3">
            <div className="access-devices__item mb-2">
                <Text className="is-flex is-align-items-center">
                    <div className="access-devices__icon">
                        <Icon type={microphoneContent.icon} size="18" />
                    </div>
                    {microphoneContent.text}
                </Text>
                {!microphoneAccessed && <GiveAccessLink onClick={showModal} />}
            </div>
            <div className="access-devices__item">
                <Text className="is-flex is-align-items-center">
                    <div className="access-devices__icon">
                        <Icon type={videoContent.icon} size="18" />
                    </div>
                    {videoContent.text}
                </Text>
                {!cameraAccessed && <GiveAccessLink onClick={showModal} />}
            </div>
            <CSSTransition
                classNames="fade-ease-out"
                timeout={100}
                in={showAccessModal}
                appear
                unmountOnExit
                onEntered={showModal}
                onExit={hideModal}
            >
                <div className="access-devices__modal" ref={modalRef}>
                    <Text isAccent className="is-block mb-3">
                        Как дать доступ к&nbsp;устройствам в&nbsp;браузере
                    </Text>
                    {toolTipText}
                    <Button
                        isFullwidth
                        className="after-call__transparent-button mt-3"
                        onClick={hideModal}
                    >
                        Понятно
                    </Button>
                </div>
            </CSSTransition>
        </div>
    )
}
